// @ts-nocheck
/**
 * New Agent Announcement — redesigned to obd3-gui pattern (ScrollPage +
 * PageHeader + Card sections). Simple heading + content form.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { ArrowLeft, Loader2, Megaphone, Save } from 'lucide-react';
import { postApiCall } from '@/service/httpCall';
import { API_AGENT_ANNOUNCEMENT_CREATE } from '@/service/url';
import { ROUTES } from '@/utilities/constant';
import { useAuth } from '@/views/dashboard/auth/auth-utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';

const AgentAnnouncementCreate = () => {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [heading, setHeading] = useState('');
  const [content, setContent] = useState('');
  const [status, setStatus] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    checkAuth(
      () => {},
      () => {},
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitForm = async (e?: any) => {
    e?.preventDefault();
    if (!heading.trim() || !content.trim()) {
      toast.warning('All fields are required');
      return;
    }
    setSaving(true);
    postApiCall(API_AGENT_ANNOUNCEMENT_CREATE, { heading, content, status })
      .then(() => {
        toast.success('Announcement created successfully');
        setTimeout(() => navigate(`/${ROUTES.AGENT_ANNOUNCEMENT.LIST}`), 1000);
      })
      .catch((err: any) => {
        toast.error(friendlyApiError(err) || 'Something went wrong');
        setSaving(false);
      });
  };

  return (
    <ScrollPage>
      <PageHeader
        title="New Agent Announcement"
        description="Broadcast a message that will appear inside the CTI panel."
        actions={
          <Button asChild variant="outline">
            <Link to={`/${ROUTES.AGENT_ANNOUNCEMENT.LIST}`}>
              <ArrowLeft className="h-4 w-4" />
              Back
            </Link>
          </Button>
        }
      />

      <form className="space-y-4" onSubmit={submitForm}>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Megaphone className="h-4 w-4 text-primary" />
              Announcement
            </CardTitle>
            <CardDescription>
              A short heading and a longer body — visible to every logged-in agent.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="heading">
                Heading <span className="text-destructive">*</span>
              </Label>
              <Input
                id="heading"
                type="text"
                value={heading}
                onChange={(e) => setHeading(e.target.value)}
                placeholder="e.g. Scheduled downtime tonight"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="content">
                Content <span className="text-destructive">*</span>
              </Label>
              <Textarea
                id="content"
                rows={5}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Enter the full message the agents will see."
              />
            </div>
            <div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
              <div className="space-y-0.5">
                <Label htmlFor="status">Status</Label>
                <p className="text-xs text-muted-foreground">
                  Only active announcements are shown to agents.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-muted-foreground">
                  {status ? 'Active' : 'Inactive'}
                </span>
                <Switch id="status" checked={status} onCheckedChange={setStatus} />
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex items-center justify-end gap-2">
          <Button asChild variant="ghost">
            <Link to={`/${ROUTES.AGENT_ANNOUNCEMENT.LIST}`}>Cancel</Link>
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {saving ? 'Adding…' : 'Add announcement'}
          </Button>
        </div>
      </form>
    </ScrollPage>
  );
};

export default AgentAnnouncementCreate;
